import { useEffect, useMemo } from 'react'
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router'
import { Spinner, holdsPlan } from '@structural-climbing/ui'
import { DEFAULT_GRADE_CONFIG, enabledDisciplines } from '@structural-climbing/validation'
import { useGymBranding } from '../features/gym/useGymBranding'
import { useGymTheme } from '../features/gym/useGymTheme'
import { GymGradeConfigContext } from '../features/gym/useGymGradeConfig'
import { GymDisciplinesContext } from '../features/gym/useGymDisciplines'
import { disciplineForPath, disciplinePath } from '../features/nav/navGroups'
import { useMyMemberships } from '../features/gym/useMyMemberships'
import { useViewedGym } from '../context/ViewedGymContext'
import { useMyProfile } from '../features/profile/useMyProfile'
import { PhoneNeededBanner } from '../features/profile/PhoneNeededBanner'
import { EmailVerificationBanner } from '../features/account/EmailVerificationBanner'

export interface GymContext {
  gymId: string
  gymSlug: string
  gymName: string
  hasActiveSubscription: boolean
  /** A child's own login: climbing screens only. See `useMyMemberships`. */
  isChildAccount: boolean
}

// The gym screens a child's own login does not get. The dashboard is the
// membership and the check-in QR, which stay with the parent.
const CHILD_HIDDEN_SEGMENTS = new Set(['dashboard', 'news', 'events', 'classes'])

export function GymLayout() {
  const { gymSlug } = useParams<{ gymSlug: string }>()
  const navigate = useNavigate()
  const { setGym: setViewedGym } = useViewedGym()
  const apiUrl = import.meta.env.VITE_API_URL as string

  // Shared cache, so a plan cancelled at the desk reaches `hasActiveSubscription`
  // the moment the realtime listener invalidates the key — no reload, and no
  // second copy of this request.
  const { memberships, childAccount, loading } = useMyMemberships()
  const { pathname, search } = useLocation()

  const context: GymContext | undefined = useMemo(() => {
    const entry = memberships.find((m) => m.gym.slug === gymSlug)
    if (!entry) return undefined
    return {
      gymId: entry.gym.id,
      gymSlug: entry.gym.slug,
      gymName: entry.gym.name,
      // A child's plan is held by the parent, so the server works out where the
      // child may log and says so; there is no subscription on the entry to read.
      hasActiveSubscription: childAccount
        ? childAccount.canLogGymIds.includes(entry.gym.id)
        : holdsPlan(entry.subscription?.effective_status) ||
          entry.subscription?.is_checked_in === true,
      isChildAccount: childAccount !== null,
    }
  }, [memberships, childAccount, gymSlug])

  const branding = useGymBranding(context?.gymId ?? null, apiUrl)
  // Both until the gym's info has loaded, which is what every gym had before.
  const disciplines = useMemo(
    () => enabledDisciplines(branding?.disciplines),
    [branding?.disciplines],
  )
  useGymTheme(branding?.primary_color ?? null, branding?.secondary_color ?? null)
  const { profile } = useMyProfile()

  useEffect(() => {
    // Only once the list has actually loaded. Bouncing on an in-flight
    // revalidation would throw the member out of the gym they are looking at.
    if (!loading && !context) void navigate('/', { replace: true })
  }, [loading, context, navigate])

  useEffect(() => {
    if (context) setViewedGym({ id: context.gymId, slug: context.gymSlug })
    // setViewedGym is not stable; the resolved gym is what should drive this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context?.gymId, context?.gymSlug])

  if (!context) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size="lg" color="primary" />
      </div>
    )
  }

  if (context.isChildAccount && CHILD_HIDDEN_SEGMENTS.has(pathname.split('/')[2] ?? ''))
    return <Navigate to={`/${context.gymSlug}/${disciplinePath(disciplines[0])}/routes`} replace />

  // A link to a discipline this gym does not have: an old bookmark, or one of
  // the places that still point at bouldering by default. Same page, the
  // discipline the gym does have.
  const segments = pathname.split('/')
  const asked = disciplineForPath(segments[2])
  if (asked && !disciplines.includes(asked)) {
    segments[2] = disciplinePath(disciplines[0])
    return <Navigate to={segments.join('/') + search} replace />
  }

  // Only on this gym's screens, and only once the profile has actually loaded
  // and says there is no phone. A child's login cannot edit its profile, so it
  // is never asked.
  const askForPhone =
    branding?.collect_missing_phones === true &&
    profile !== null &&
    !profile.phone &&
    !context.isChildAccount

  // Strictly `false`: a Worker that does not say is not a member who is not
  // verified. A child's login never signs, so it is never asked.
  const askForEmail =
    profile !== null &&
    profile.emailVerified === false &&
    !!profile.email &&
    !context.isChildAccount

  return (
    <GymGradeConfigContext.Provider value={branding?.grade_config ?? DEFAULT_GRADE_CONFIG}>
      <GymDisciplinesContext.Provider value={disciplines}>
        {askForEmail && <EmailVerificationBanner />}
        {askForPhone && <PhoneNeededBanner gymName={context.gymName} />}
        <Outlet context={context} />
      </GymDisciplinesContext.Provider>
    </GymGradeConfigContext.Provider>
  )
}
